import { Button } from '@/components/ui/button';
import {
  AuthNotice,
  AuthSpinner,
  AUTH_PRIMARY_BUTTON_CLASS,
  AUTH_SECONDARY_BUTTON_CLASS,
} from '@/features/auth-shell';
import { Icon } from '@/lib/icons';
import { ResetPasswordCopy } from './reset-password.types';
import { ResetPasswordCard, StatusIcon } from './ResetPasswordLayout';

type ResetPasswordStatusPanelProps = {
  t: ResetPasswordCopy;
  state: 'verifying' | 'invalid' | 'success';
  onRequestNewLink: () => void;
  onBackToLogin: () => void;
};

export function ResetPasswordStatusPanel({
  t,
  state,
  onRequestNewLink,
  onBackToLogin,
}: ResetPasswordStatusPanelProps) {
  if (state === 'verifying') {
    return <VerifyingPanel t={t} />;
  }

  if (state === 'success') {
    return <SuccessPanel t={t} onBackToLogin={onBackToLogin} />;
  }

  return <InvalidLinkPanel t={t} onRequestNewLink={onRequestNewLink} onBackToLogin={onBackToLogin} />;
}

function VerifyingPanel({ t }: { t: ResetPasswordCopy }) {
  return (
    <ResetPasswordCard title={t.resetPasswordNewTitle} description={t.resetPasswordNewDesc} t={t}>
      <AuthNotice
        icon={<AuthSpinner className="size-5 text-brand" />}
        title={t.resetPasswordVerifying}
        testId="reset-password-verifying"
      />
    </ResetPasswordCard>
  );
}

function InvalidLinkPanel({
  t,
  onRequestNewLink,
  onBackToLogin,
}: {
  t: ResetPasswordCopy;
  onRequestNewLink: () => void;
  onBackToLogin: () => void;
}) {
  return (
    <ResetPasswordCard title={t.resetPasswordInvalidTitle} t={t}>
      <AuthNotice tone="danger" icon={<StatusIcon tone="danger" />} testId="reset-password-invalid">
        {t.resetPasswordInvalidDesc}
      </AuthNotice>

      <div className="grid gap-3">
        <Button type="button" onClick={onRequestNewLink} className={AUTH_PRIMARY_BUTTON_CLASS}>
          <span>{t.resetPasswordRequestNewLink}</span>
          <Icon name="arrowRight" className="ml-2 h-4 w-4" />
        </Button>

        <Button type="button" variant="outline" onClick={onBackToLogin} className={`${AUTH_SECONDARY_BUTTON_CLASS} w-full`}>
          <Icon name="arrowLeft" className="mr-2 h-4 w-4" />
          {t.backToLogin}
        </Button>
      </div>
    </ResetPasswordCard>
  );
}

function SuccessPanel({ t, onBackToLogin }: { t: ResetPasswordCopy; onBackToLogin: () => void }) {
  return (
    <ResetPasswordCard title={t.resetPasswordSuccessTitle} t={t}>
      <AuthNotice tone="success" icon={<StatusIcon tone="success" />} testId="reset-password-success">
        <p>{t.resetPasswordSuccessDesc}</p>
        <p className="mt-2 flex items-center gap-2 text-xs">
          <AuthSpinner className="text-brand" />
          <span>{t.resetPasswordRedirecting}</span>
        </p>
      </AuthNotice>

      <Button type="button" onClick={onBackToLogin} className={AUTH_PRIMARY_BUTTON_CLASS}>
        {t.resetPasswordGoToLogin}
        <Icon name="arrowRight" className="ml-2 h-4 w-4" />
      </Button>
    </ResetPasswordCard>
  );
}
